import { useEffect, type ReactNode } from 'react';

export interface ContactSheetAction {
  label: string;
  icon: ReactNode;
  /** When set, label rendered with this Tailwind text color (e.g. text-red-600) */
  destructive?: boolean;
  onAction: () => void;
}

interface ContactActionSheetProps {
  open: boolean;
  contactName: string;
  contactPhone?: string;
  actions: ContactSheetAction[];
  onClose: () => void;
}

/**
 * ContactActionSheet — bottom sheet that pops up on long-press of a
 * conversation row, exposing the full set of actions in one tap.
 *
 * Why long-press AND swipe both? Swipe gives the most-used actions in two
 * gestures (Pin/Read on right swipe, Call/Archive on left). Long-press is
 * the discovery surface — every action visible without remembering which
 * direction reveals what. iOS Mail / Things / Apple Notes all do this.
 *
 * P3.3 polish (Ernie 2026-05-03).
 */
export function ContactActionSheet({
  open,
  contactName,
  contactPhone,
  actions,
  onClose,
}: ContactActionSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-sheet-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink-900/40 backdrop-blur-sm animate-ss-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-3xl bg-white dark:bg-[#0F172A] pt-2 pb-[max(env(safe-area-inset-bottom,12px),16px)] shadow-ss-md animate-ss-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle indicator (visual cue this is a sheet) */}
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-ink-200 dark:bg-[#334155]" />

        {/* Header */}
        <div className="px-5 pb-3 pt-1">
          <div
            id="contact-sheet-title"
            className="truncate text-base font-bold text-ink-900 dark:text-[#E2E8F0]"
          >
            {contactName}
          </div>
          {contactPhone ? (
            <div className="text-xs text-ink-500 dark:text-[#94A3B8]">{contactPhone}</div>
          ) : null}
        </div>

        {/* Actions */}
        <ul className="border-t border-ink-100 dark:border-[#334155]">
          {actions.map((a, i) => (
            <li
              key={i}
              className={i < actions.length - 1 ? 'border-b border-ink-100 dark:border-[#334155]' : ''}
            >
              <button
                type="button"
                onClick={() => {
                  onClose();
                  // Defer the action so the sheet visually closes first.
                  window.setTimeout(() => a.onAction(), 120);
                }}
                className={[
                  'flex w-full items-center gap-3 px-5 py-3.5 text-left text-[15px] font-semibold',
                  'transition-colors active:bg-ink-100 dark:active:bg-[#1E293B]',
                  a.destructive
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-ink-900 dark:text-[#E2E8F0]',
                ].join(' ')}
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center" aria-hidden="true">
                  {a.icon}
                </span>
                {a.label}
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={onClose}
              className="flex w-full items-center justify-center px-5 py-3.5 text-[15px] font-semibold text-ink-500 dark:text-[#94A3B8] active:bg-ink-100 dark:active:bg-[#1E293B]"
            >
              Cancel
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}
